import { isBuiltinQuestion, isQuestionEnabled, type CardInfo } from '@lub0v/santa-utils';
import { useTranslation } from 'react-i18next';
import { Typography } from '@src/components';
import { CustomAnswers } from './CustomQuestions';

/** Shared questionnaire content for organizer preview and Help Desk. */
export function OrganizerQuestionnaire({ card }: {
  card: Pick<CardInfo, 'customAnswers' | 'hiddenAnswers'> & Partial<Pick<CardInfo, 'wish'>>;
}) {
  const { t } = useTranslation();
  const text = (key: string) => t(`box.organizer.preview.${key}`);
  const hiddenAnswers = (card.hiddenAnswers ?? []).filter(
    (answer) => !card.customAnswers?.some((visible) => visible.fieldId === answer.field.id),
  );
  return (
    <>
      {[
        ...(card.customAnswers ?? []).map((answer) => ({ field: answer.field, answer })),
        ...hiddenAnswers.map((hidden) => ({ field: hidden.field, hidden })),
      ]
        .filter((entry) => isQuestionEnabled(entry.field))
        .sort((a, b) => a.field.position - b.field.position)
        .map((entry, questionIndex) => {
          const { field } = entry;
          if ('answer' in entry) {
            const answer = entry.answer;
            return (
              <CustomAnswers
                key={field.id}
                showEmpty
                startNumber={questionIndex + 1}
                answers={[
                  field.type === 'santa_letter' && !answer.value?.trim() && card.wish?.trim()
                    ? { ...answer, value: card.wish }
                    : answer,
                ]}
              />
            );
          }
          const hasValue = entry.hidden.filled;
          return (
            <section key={`hidden-${field.id}`} data-testid="participant-hidden-answer">
              <Typography as="h3" role="title/card">
                {questionIndex + 1}.{' '}
                {isBuiltinQuestion(field)
                  ? t(
                      `questions.types.${field.type === 'delivery_address' && field.config.deliveryMode === 'pickup' ? 'pickup_point' : field.type}`,
                    )
                  : field.title}
              </Typography>
              <Typography as="p" role="body/base">
                {hasValue ? `${text('filled')} · ••••••` : text('empty')}
              </Typography>
              {hasValue && (
                <Typography as="p" role="meta/helper">
                  {t('questions.hiddenFromOrganizer')}
                </Typography>
              )}
            </section>
          );
        })}
    </>
  );
}
